import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { message } from 'antd';
import {
  DOLLAR_JOURNAL,
  UPDATE_DOLLAR_METER_DATA_BULK,
  DOLLAR_JOURNAL_BULK_APPROVAL_JOB_STATUS,
} from 'graphql/dollarJournalDashboard.gql';
import type { MeterRow, SaveFeedback } from '@custom-types/dollarJournalDashboard';
import type { AsyncApprovalFeedback } from '@custom-types/prodStat';
import useSession from '@hooks/useSession';
import { getRuntimeEnv } from '@utils/runtimeEnv';

export const useDollarJournalDashboardGraphQl = ({ monthYear }: { monthYear?}) => {
  const [tableData, setTableData] = useState<MeterRow[]>([]);
  const [originalData, setOriginalData] = useState<MeterRow[]>([]); // Track original server data
  const [pendingChanges, setPendingChanges] = useState<Record<string, Partial<MeterRow>>>({});
  const [saveFeedback, setSaveFeedback] = useState<SaveFeedback | null>(null);
  const [asyncApproval, setAsyncApproval] = useState<AsyncApprovalFeedback | null>(null);
  const [bulkApprovalSelection, setBulkApprovalSelection] = useState<Set<number>>(new Set());
  const { isAdmin, isManager, isSuperAdmin, isUser, account } = useSession();
  const username = account?.name || account?.username || '';

  // Fetch main data
  const { data, loading, refetch } = useQuery(DOLLAR_JOURNAL, {
    variables: { monthYear },
    fetchPolicy: 'network-only',
  });

  // Initialize table data when data arrives
  useEffect(() => {
    if (data) {
      const rows: MeterRow[] = data.dollarJournal ?? [];
      setTableData(rows);
      setOriginalData(rows); // Store original data from server
      setBulkApprovalSelection(new Set()); // clear on data reload
    }
  }, [data]);

  // Refetch when monthYear changes
  useEffect(() => {
    if (monthYear) {
      refetch({ monthYear });
    }
  }, [monthYear, refetch]);

  const [updateMeterDataBulk, { loading: saving }] = useMutation(UPDATE_DOLLAR_METER_DATA_BULK);

  // ─── Async bulk-update status polling (WTTP-1012, reusing WTTP-933's async wrapper) ───
  // After a multi-meter approval batch is queued, poll the job status every 10s. On
  // success, refetch rather than patching rows in place — the existing synchronous save
  // path below already refetches after a save, so this stays consistent with that.
  const apolloClient = useApolloClient();
  const [pollJobId, setPollJobId] = useState<number | null>(null);
  const pollCountRef = useRef<number>(0);
  // Whether the in-flight job is an approval action vs. a pure "Ready for Approval"
  // status change — picks which banner copy applies on completion.
  const pollIsApprovalRef = useRef<boolean>(true);

  useEffect(() => {
    if (pollJobId == null) return;
    let cancelled = false;

    const checkStatus = async () => {
      try {
        const { data: statusData } = await apolloClient.query({
          query: DOLLAR_JOURNAL_BULK_APPROVAL_JOB_STATUS,
          variables: { jobId: pollJobId },
          fetchPolicy: 'network-only',
        });
        if (cancelled) return;
        const job = statusData?.dollarJournalBulkApprovalJobStatus;
        if (!job) return;

        if (job.status === 'succeeded') {
          await refetch();
          const count = pollCountRef.current;
          setAsyncApproval({
            status: 'completed',
            message: pollIsApprovalRef.current
              ? `Approval completed for ${count} meter${count === 1 ? '' : 's'}.`
              : `Status updated for ${count} meter${count === 1 ? '' : 's'}.`,
          });
          setPollJobId(null);
        } else if (job.status === 'failed') {
          setAsyncApproval({ status: 'failed', message: job.errorMessage || 'Bulk update failed' });
          message.error(job.errorMessage || 'Bulk update failed');
          setPollJobId(null);
        }
        // queued / processing → keep polling
      } catch (err) {
        // Transient error (network blip, etc.) — the next tick retries.
        console.error('Dollar Journal bulk update status poll failed:', err);
      }
    };

    checkStatus(); // check once immediately, then on an interval
    const intervalId = setInterval(checkStatus, 10000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [pollJobId, apolloClient, refetch]);

  const handleEdit = (id: number, field: string, value: unknown) => {
    setTableData((prev) =>
      prev.map((row) =>
        row.meter_computed_id === id ? ({ ...row, [field]: value } as MeterRow) : row
      )
    );
    setPendingChanges((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value } as Partial<MeterRow>,
    }));
  };

  const handleSave = async () => {
    const updates = Object.keys(pendingChanges).map((id) => ({
      meter_computed_id: Number(id),
      ...pendingChanges[id],
    }));

    if (!updates.length) {
      message.info('No changes to save.');
      return;
    }

    setSaveFeedback(null);
    setAsyncApproval(null);

    // WTTP-1012 (reusing WTTP-933's async wrapper): only route through the background
    // job when the batch actually contains an approval and is large enough that the
    // synchronous path risks the same timeout WTTP-1012 found (that heavy per-meter
    // invoice calculation only runs on the approval branch). Everything else — small
    // batches, and any batch that's purely a Ready-for-Approval status change — stays
    // on the original synchronous path below, unchanged.
    const ASYNC_BATCH_THRESHOLD = 15;
    const asyncEnabled = getRuntimeEnv('REACT_APP_DOLLARJOURNAL_BULK_APPROVAL_ASYNC_ENABLED') === 'true';
    const hasApprovals = updates.some((u) => u.journal_approved_status === true);
    const processAsync = asyncEnabled && hasApprovals && updates.length > ASYNC_BATCH_THRESHOLD;
    // Only include processAsync in the call when true, so the synchronous path's
    // request is byte-for-byte identical to before this change.
    const variables = processAsync ? { updates, processAsync } : { updates };

    try {
      const { data } = await updateMeterDataBulk({ variables });

      if (data.updateDollarMeterDataBulk?.queued) {
        const jobId = data.updateDollarMeterDataBulk.jobId as number;
        setPendingChanges({});
        setBulkApprovalSelection(new Set());
        pollCountRef.current = updates.length;
        pollIsApprovalRef.current = hasApprovals;
        setAsyncApproval({
          status: 'processing',
          message: hasApprovals
            ? 'Approval request is being processed. This can take up to a minute'
            : 'Status change request is being processed. This can take up to a minute',
        });
        setPollJobId(jobId);
        return;
      }

      if (data.updateDollarMeterDataBulk?.approved) {
        setPendingChanges({});
        await refetch();
        setSaveFeedback({
          status: 'success',
          message: `Saved ${updates.length} updates successfully!`,
        });
        message.success(`Saved ${updates.length} updates successfully!`);
      } else {
        setSaveFeedback({ status: 'error', message: 'Failed to save updates' });
        message.error('Failed to save updates');
      }
    } catch (err: any) {
      setSaveFeedback({ status: 'error', message: err.message || 'Failed to save updates' });
      message.error(err.message || 'Failed to save updates');
    }
  };

  // Helper function to check if a row was originally approved (from saved server data)
  const isRowOriginallyApproved = (id: number): boolean => {
    const originalRow = originalData.find((row) => row.meter_computed_id === id);
    return Boolean(originalRow?.journal_approved_status);
  };

  // ─── Bulk approval helpers ────────────────────────────────────────────────────

  const bulkApprovableIds = (rows: MeterRow[]): number[] =>
    rows
      .filter(
        (row) =>
          row.journal_ready_for_approval === 'Ready' &&
          !originalData.find((r) => r.meter_computed_id === row.meter_computed_id)
              ?.journal_approved_status
      )
      .map((row) => row.meter_computed_id);

  const getBulkApproveHeaderState = () => {
    const eligible = bulkApprovableIds(tableData);
    if (eligible.length === 0) return { checked: false, indeterminate: false };
    const selectedCount = eligible.filter((id) => bulkApprovalSelection.has(id)).length;
    return {
      checked: selectedCount === eligible.length,
      indeterminate: selectedCount > 0 && selectedCount < eligible.length,
    };
  };

  const handleBulkApproveHeaderChange = (checked: boolean) => {
    const eligible = bulkApprovableIds(tableData);
    if (checked) {
      setBulkApprovalSelection(new Set(eligible));
      setTableData((prev) =>
        prev.map((row) =>
          eligible.includes(row.meter_computed_id)
            ? {
              ...row,
              journal_approved_status: true,
              journal_approver_signature: row.journal_approver_signature || username,
            }
            : row
        )
      );
      const signatureByEligibleId = new Map(
        tableData
          .filter((r) => eligible.includes(r.meter_computed_id))
          .map((r) => [r.meter_computed_id, r.journal_approver_signature || username])
      );
      setPendingChanges((prev) => {
        const next = { ...prev };
        eligible.forEach((id) => {
          next[id] = {
            ...next[id],
            journal_approved_status: true,
            journal_approver_signature: signatureByEligibleId.get(id) ?? username,
          };
        });
        return next;
      });
    } else {
      setBulkApprovalSelection(new Set());
      const originalById = new Map(
        originalData.map((r) => [r.meter_computed_id, r])
      );
      setTableData((prev) =>
        prev.map((row) => {
          if (!eligible.includes(row.meter_computed_id)) return row;
          const original = originalById.get(row.meter_computed_id);
          return original
            ? {
              ...row,
              journal_approved_status: original.journal_approved_status,
              journal_approver_signature: original.journal_approver_signature,
            }
            : row;
        })
      );
      setPendingChanges((prev) => {
        const next = { ...prev };
        eligible.forEach((id) => {
          if (next[id]) {
            delete next[id].journal_approved_status;
            delete next[id].journal_approver_signature;
            if (Object.keys(next[id]).length === 0) delete next[id];
          }
        });
        return next;
      });
    }
  };

  // ─── Bulk "Ready for Approval" helpers (preparer) ────────────────────────────

  // All non-persisted rows — eligible for both check and uncheck
  const bulkReadyEligibleIds = (rows: MeterRow[]): number[] =>
    rows
      .filter((row) => !isRowOriginallyApproved(row.meter_computed_id))
      .map((row) => row.meter_computed_id);

  const getBulkReadyHeaderState = () => {
    const eligible = bulkReadyEligibleIds(tableData);
    if (eligible.length === 0) return { checked: false, indeterminate: false, disabled: true };
    const readyCount = tableData.filter(
      (r) => eligible.includes(r.meter_computed_id) && r.journal_ready_for_approval === 'Ready'
    ).length;
    return {
      checked: readyCount === eligible.length,
      indeterminate: readyCount > 0 && readyCount < eligible.length,
      disabled: false,
    };
  };

  const handleBulkReadyHeaderChange = (checked: boolean) => {
    const eligible = bulkReadyEligibleIds(tableData);
    setTableData((prev) =>
      prev.map((row) =>
        eligible.includes(row.meter_computed_id)
          ? { ...row, journal_ready_for_approval: checked ? 'Ready' : 'In Progress' }
          : row
      )
    );
    setPendingChanges((prev) => {
      const next = { ...prev };
      eligible.forEach((id) => {
        next[id] = { ...next[id], journal_ready_for_approval: checked ? 'Ready' : 'In Progress' };
      });
      return next;
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────

  const isSaveDisabled = Object.keys(pendingChanges).length === 0;

  return {
    data: tableData,
    loading,
    isAdmin,
    isSuperAdmin,
    isManager,
    isUser,
    saving,
    saveFeedback,
    setSaveFeedback,
    asyncApproval,
    setAsyncApproval,
    handleEdit,
    handleSave,
    refetch,
    isRowOriginallyApproved,
    username,
    getBulkApproveHeaderState,
    handleBulkApproveHeaderChange,
    bulkApprovalSelection,
    isSaveDisabled,
    getBulkReadyHeaderState,
    handleBulkReadyHeaderChange,
  };
};
